/**
 * Launch Mode — MVP scope reduction.
 *
 * Временный режим для запуска: оставляет в UI только организации и турниры.
 * Скрывает из навигации и блокирует прямые URL для всех остальных фичей,
 * чтобы первые пользователи не наткнулись на полуготовое.
 *
 * Чтобы вернуть полный функционал — поставить LAUNCH_MODE_MVP = false (или
 * `NEXT_PUBLIC_LAUNCH_MODE_MVP=0` в окружении).
 *
 * Бэкенд-эндпоинты НЕ затрагиваются: серверу всё равно, кто к нему ходит,
 * Tauri и прямые API-вызовы продолжают работать.
 */

const ENV_OVERRIDE = process.env["NEXT_PUBLIC_LAUNCH_MODE_MVP"];

/**
 * Default: ON. Override через env-переменную `NEXT_PUBLIC_LAUNCH_MODE_MVP`:
 * - "0" / "false" → выключить MVP-mode, вернуть полный набор
 * - что угодно ещё или unset → MVP-mode включён
 */
export const LAUNCH_MODE_MVP =
  ENV_OVERRIDE !== "0" && ENV_OVERRIDE?.toLowerCase() !== "false";

/**
 * Префиксы путей, которые скрываются в MVP-режиме.
 *
 * Точное совпадение (`pathname === prefix`) ИЛИ префикс с `/` после
 * (`pathname.startsWith(prefix + "/")`). Это значит:
 * - `/scrims` и `/scrims/my` будут скрыты
 * - но `/scrimshield` (если бы был) — НЕ затронут
 */
export const MVP_HIDDEN_ROUTE_PREFIXES: readonly string[] = [
  "/scrims",
  // "/messages" и "/chat" — раздел открыт (KAN-876). Портал переписки и все кнопки
  // «Написать» гейтятся этим же списком, поэтому включились вместе с ним.
  // "/lfg" — раздел открыт: доска поиска состава доступна всем (KAN-803).
  "/streamerboards",
  "/shop",
  "/leaderboard",
  // "/rosters" — раздел разблокирован (D1): публичный листинг команд в хедере
  "/sandbox",
  "/support", // в MVP заменяем тикетную систему Discord-каналом
  // Десктопное приложение пока не раздаём широко: пункт «Скачать» убран из шапки
  // и футера, прямая ссылка уводит на /tournaments. Автообновление уже
  // установленных копий это не задевает — оно ходит в /api/tauri-update,
  // а установщик отдаёт /api/desktop-download; оба под этот префикс не попадают.
  "/download",
];

/**
 * Куда редиректить, если юзер пришёл по прямой ссылке на скрытый route.
 * Не 404, чтобы не пугать — отправляем на главный флоу.
 */
export const MVP_REDIRECT_TARGET = "/tournaments";

/**
 * Проверка: скрыт ли данный pathname в MVP-режиме.
 * Использовать в навигации (хедер, сайдбары) и middleware.
 */
export function isHiddenInMvp(pathname: string): boolean {
  if (!LAUNCH_MODE_MVP) return false;
  return MVP_HIDDEN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}
